
/**
 * Image API Service
 * Handles all API communication for the Image Generator
 */
class ImageApiService {
  constructor(options = {}) {
    this.customerId = options.customerId;
    this.productId = options.productId;
    this.fingerprint = options.fingerprint;
    this.nextCursor = null;
    this.loadedImageIds = new Set(); // Track loaded image IDs to prevent duplicates
  }
  
  /**
   * Load images from the API
   * @param {Object} options - Options for loading images
   * @param {boolean} options.reset - Whether to reset pagination and load from beginning
   * @param {boolean} options.onlyThisProduct - Whether to filter only by current product
   * @returns {Promise<Object>} Object with images array and pagination info
   */
  async loadImagesFromAPI(options = {}) {
    try {
      if (!this.customerId) {
        console.log('Cannot load images: No customer ID found');
        return { images: [], hasMore: false };
      }

      // Reset cursor if requested
      if (options.reset) {
        this.nextCursor = null;
        this.loadedImageIds.clear(); // Clear tracking of loaded images
        console.log('Pagination reset: cleared loaded image IDs set');
      }

      const onlyThisProduct = options.onlyThisProduct || false;
      console.log('Loading images from API for user:', this.customerId, 
        this.nextCursor ? `with cursor: ${this.nextCursor}` : 'from beginning',
        `tracked IDs count: ${this.loadedImageIds.size}`,
        `onlyThisProduct: ${onlyThisProduct}`);
      
      // Add timestamp for cache avoidance
      const timestamp = Date.now();
      
      // Construct URL for the get-images endpoint with the action parameter
      const apiUrl = `/apps/image-api/?action=get&t=${timestamp}`;
      
      // Full URL with all parameters
      const fullApiUrl = new URL(apiUrl, window.location.origin);
      
      // Add logged_in_customer_id for App Proxy authentication
      if (this.customerId) {
        fullApiUrl.searchParams.set('logged_in_customer_id', this.customerId);
      }

      // Ensure 'shop' parameter is present (necessary for App Proxy)
      if (!fullApiUrl.searchParams.has('shop')) {
        const shopDomain = window.Shopify?.shop || window.location.hostname;
        fullApiUrl.searchParams.set('shop', shopDomain);
      }
      
      // Add product_id filter if available
      if (this.productId) {
        fullApiUrl.searchParams.set('product_id', this.productId);
      }
      
      // UPDATED: Add onlyThisProduct filter - this controls API behavior
      if (onlyThisProduct && this.productId) {
        fullApiUrl.searchParams.set('only_this_product', 'true');
        console.log('Applied only_this_product filter for product:', this.productId);
      } else {
        // Explicitly set to false when not filtering to ensure API behavior
        fullApiUrl.searchParams.set('only_this_product', 'false');
        console.log('Product filter disabled, showing all user images');
      }
      
      // Add cursor for pagination if available
      if (this.nextCursor) {
        fullApiUrl.searchParams.set('cursor', this.nextCursor);
      }
      
      console.log('Fetching images from:', fullApiUrl.toString());
      
      const response = await fetch(fullApiUrl.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        }
      });
      
      if (!response.ok) {
        let errorText;
        try {
          const errorData = await response.json();
          errorText = errorData.error || `Erro ${response.status}`;
          console.error('Error data from server:', errorData);
        } catch (e) {
          errorText = `Erro ${response.status} ao carregar imagens`;
        }
        throw new Error(errorText);
      }
      
      const data = await response.json();
      console.log('API response:', data);
      
      // Store the next cursor for subsequent requests
      this.nextCursor = data.next_cursor || null;
      
      const hasMore = !!this.nextCursor;
      console.log(`Received ${data.data?.length || 0} images, has more: ${hasMore}`);
      
      // Filter out any images we've already loaded (by ID)
      let images = [];
      if (data.data && Array.isArray(data.data)) {
        // Only track uniqueness for actual objects with IDs
        images = data.data.filter(img => {
          if (typeof img === 'object' && img.id) {
            if (this.loadedImageIds.has(img.id)) {
              console.log(`Skipping already loaded image with ID: ${img.id}`);
              return false; // Skip if already loaded
            }
            this.loadedImageIds.add(img.id); // Add to set of loaded IDs
            
            return true;
          }
          return img; // Keep string URLs as is
        }).map(img => {
          // If it's an object, ensure we only have necessary fields and use original_prompt
          if (typeof img === 'object' && img.id) {
            return {
              id: img.id,
              image_url: img.image_url,
              prompt: img.original_prompt || '', // Use original_prompt for copy functionality
              created_at: img.created_at,
              is_featured: !!img.is_featured,
              // Add backward compatibility fields that might be expected by frontend
              user_id: this.customerId,
              product_id: this.productId
            };
          }
          return img; // Keep string URLs as is
        });
        
        console.log(`Filtered to ${images.length} unique images out of ${data.data.length} received`);
        console.log(`Total tracked image IDs: ${this.loadedImageIds.size}`);
        console.log('Sample processed image:', images[0] ? {
          id: images[0].id,
          has_prompt: !!images[0].prompt,
          is_featured: images[0].is_featured
        } : 'No images');
        
        return { 
          images: images,
          hasMore: hasMore
        };
      }
      
      return { images: [], hasMore: false };
    } catch (error) {
      console.error('Error loading images from API:', error);
      return { images: [], hasMore: false };
    }
  }
  
  /**
   * Reset pagination and start from the beginning
   */
  resetPagination() {
    this.nextCursor = null;
    this.loadedImageIds.clear();
  }
  
  /**
   * Generate a new image
   * @param {string} payloadString The JSON string representing the payload from ImageGenerator
   * @returns {Promise<Object>} The generated image data
   */
  async generateImage(payloadString) {
    if (!this.customerId && !this.fingerprint) {
      throw new Error('Não foi possível identificar seu dispositivo. Tente novamente em instantes.');
    }

    let requestBody;
    let originalPromptForReturn; 

    try {
      const parsedPayload = JSON.parse(payloadString);
      console.log('[ImageApiService] Received payload from ImageGenerator:', parsedPayload);

      // Define product types that are treated as color card based generation
      const colorCardLikeProductTypes = ['color_card', 'card_color', 'image_card']; 
      
      // Define product types that support image upload
      const imageUploadProductTypes = ['image_card_with_upload'];

      if (colorCardLikeProductTypes.includes(parsedPayload.type)) {
        if (!parsedPayload.colorName) {
          console.error(`[ImageApiService] ${parsedPayload.type} generation error: colorName is missing in payload.`, parsedPayload);
          throw new Error(`Dados do ${parsedPayload.type} inválidos: nome da cor ausente.`);
        }
        
        requestBody = {
          type: parsedPayload.type,
          colorName: parsedPayload.colorName,
          productId: this.productId,
          customerId: this.customerId,
        };
        originalPromptForReturn = parsedPayload.colorName; 
        console.log(`[ImageApiService] Preparing ${parsedPayload.type} request for Edge Function:`, requestBody);

      } else if (imageUploadProductTypes.includes(parsedPayload.type)) {
        // Handle image_card_with_upload type
        if (!parsedPayload.colorName) {
          console.error(`[ImageApiService] ${parsedPayload.type} generation error: colorName is missing in payload.`, parsedPayload);
          throw new Error('Dados do card de imagem inválidos: nome da combinação ausente.');
        }
        
        requestBody = {
          type: parsedPayload.type,
          colorName: parsedPayload.colorName,
          productId: this.productId,
          customerId: this.customerId,
        };
        
        // Include uploaded image if available
        if (parsedPayload.inputImage) {
          requestBody.inputImage = parsedPayload.inputImage;
          console.log('[ImageApiService] Including uploaded image in request');
        } else {
          console.log('[ImageApiService] No inputImage provided for image_card_with_upload');
        }
        
        originalPromptForReturn = parsedPayload.colorName; 
        console.log(`[ImageApiService] Preparing ${parsedPayload.type} request for Edge Function:`, requestBody);

      } else if (parsedPayload.type === 'default') {
        // This is for custom text prompts
        if (typeof parsedPayload.prompt !== 'string' || !parsedPayload.prompt.trim()) {
          console.error('[ImageApiService] Default generation error: prompt is missing or invalid in payload.', parsedPayload);
          throw new Error(window.t('toasts.enter_item_required'));
        }
        requestBody = {
          type: 'default',
          inputs: { 
            input1: parsedPayload.prompt 
          },
          productId: this.productId,
          customerId: this.customerId,
        };
        originalPromptForReturn = parsedPayload.prompt; 
        console.log('[ImageApiService] Preparing default request for Edge Function:', requestBody);
      
      } else {
        console.error('[ImageApiService] Generation error: Unsupported payload type received from client.', parsedPayload);
        throw new Error(`Tipo de payload não suportado: ${parsedPayload.type || 'desconhecido'}`);
      }
    } catch (e) {
      console.error('[ImageApiService] Error parsing payloadString. This should not happen if ImageGenerator sends valid JSON. Error:', e.message, 'PayloadString:', payloadString);
      throw new Error('Erro interno ao processar requisição. Dados inválidos.');
    }
    
    if (this.fingerprint) {
      requestBody.fingerprint_id = this.fingerprint;
    }
    
    console.log('[ImageApiService] Final requestBody for Edge Function:', requestBody);

    const timestamp = Date.now();
    const apiUrl = `/apps/image-api/?action=generate&t=${timestamp}`;
    const fullApiUrl = new URL(apiUrl, window.location.origin);

    if (this.customerId) {
      fullApiUrl.searchParams.set('logged_in_customer_id', this.customerId);
    }
    if (!fullApiUrl.searchParams.has('shop')) {
      const shopDomain = window.Shopify?.shop || window.location.hostname;
      fullApiUrl.searchParams.set('shop', shopDomain);
    }
    
    const response = await fetch(fullApiUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(requestBody)
    });

    if (response.redirected) {
      const redirectUrl = new URL(response.url);
      if (redirectUrl.pathname.includes('login') || redirectUrl.pathname.includes('account')) {
        throw new Error(window.t('toasts.session_expired_login'));
      }
    }

    if (!response.ok) {
      let errorData;
      let errorMessage;
      
      try {
        errorData = await response.json();
        
        // Check if we have a structured error response from the new error handling
        if (errorData.error_type && errorData.user_action) {
          console.log(`[ImageApiService] Structured error from ${errorData.provider}:`, errorData);
          
          // For specific error types, show the detailed message and user action
          if (errorData.error_type === 'content_filter') {
            errorMessage = `${errorData.error}\n\n${errorData.user_action}`;
          } else if (errorData.error_type === 'quota_exceeded') {
            errorMessage = `${errorData.error}\n\n${errorData.user_action}`;
          } else if (errorData.error_type === 'service_unavailable') {
            errorMessage = `${errorData.error}\n\n${errorData.user_action}`;
          } else if (errorData.error_type === 'invalid_input') {
            errorMessage = `${errorData.error}\n\n${errorData.user_action}`;
          } else {
            // For other error types, use the error message
            errorMessage = errorData.error;
          }
          
          // Log additional details for debugging
          if (errorData.details) {
            console.log(`[ImageApiService] Error details:`, errorData.details);
          }
          
        } else {
          // Fallback to legacy error handling
          errorMessage = errorData.error || `Erro ${response.status}`;
        }
        
      } catch (e) {
        // If we can't parse JSON, use generic message based on status code
        if (response.status === 400) {
          errorMessage = 'Dados de entrada inválidos. Verifique sua descrição e tente novamente.';
        } else if (response.status === 429) {
          errorMessage = 'Limite de geração atingido. Aguarde alguns minutos antes de tentar novamente.';
        } else if (response.status === 503) {
          errorMessage = 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.';
        } else {
          errorMessage = `Erro ${response.status} ao processar requisição`;
        }
      }
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(data.error);
    }
    
    if (data.output && Array.isArray(data.output) && data.output.length > 0) {
      return {
        id: data.id || Date.now().toString(),
        image_url: data.output[0],
        image_path: data.image_path || '',
        status: data.status || 'success',
        user_id: this.customerId, 
        product_id: this.productId, 
        prompt: originalPromptForReturn, 
        created_at: new Date().toISOString() 
      };
    }
    
    throw new Error('Formato de resposta inválido da API de geração.');
  }
  
  /**
   * Delete an image
   * @param {Object|string} imageData Image object or ID to delete
   * @returns {Promise<Object>} Result of deletion
   */
  async deleteImage(imageData) {
    // Extract image ID and URL based on type
    const imageId = typeof imageData === 'string' ? null : imageData.id;
    
    if (!imageId) {
      throw new Error('ID de imagem não fornecido');
    }
    
    // Validate that we have a customer ID for authentication
    if (!this.customerId) {
      throw new Error('ID do usuário não disponível. Faça login novamente.');
    }
    
    const timestamp = Date.now();
    const deleteUrl = `/apps/image-api/?action=delete&t=${timestamp}`;
    
    const fullDeleteUrl = new URL(deleteUrl, window.location.origin);
    
    // Add logged_in_customer_id for App Proxy authentication
    if (this.customerId) {
      fullDeleteUrl.searchParams.set('logged_in_customer_id', this.customerId);
    }
    
    // Ensure 'shop' parameter is present
    if (!fullDeleteUrl.searchParams.has('shop')) {
      const shopDomain = window.Shopify?.shop || window.location.hostname;
      fullDeleteUrl.searchParams.set('shop', shopDomain);
    }
    
    console.log('Sending delete request to:', fullDeleteUrl.toString());
    
    const response = await fetch(fullDeleteUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ 
        imageId: imageId,
        userId: this.customerId
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error deleting image:', errorData);
      
      if (errorData.error && errorData.error.includes("featured")) {
        throw new Error("Imagens em destaque não podem ser removidas");
      } else {
        throw new Error(errorData.error || `Erro ao excluir imagem (${response.status})`);
      }
    }
    
    const result = await response.json();
    console.log('Delete response:', result);
    
    return result;
  }
  
  /**
   * Check the status of an ongoing generation
   * @param {string} predictionId The ID of the prediction to check
   * @returns {Promise<Object>} Status update
   */
  async checkStatus(predictionId) {
    console.log('Checking status for prediction:', predictionId);
    
    const timestamp = Date.now();
    const statusUrl = `/apps/image-api/?action=generate&t=${timestamp}`;
    
    // Construct URL with App Proxy parameters
    const fullStatusUrl = new URL(statusUrl, window.location.origin);
    
    // Add logged_in_customer_id if available
    if (this.customerId) {
      fullStatusUrl.searchParams.set('logged_in_customer_id', this.customerId);
    }
    
    // Ensure 'shop' parameter is present
    if (!fullStatusUrl.searchParams.has('shop')) {
      const shopDomain = window.Shopify?.shop || window.location.hostname;
      fullStatusUrl.searchParams.set('shop', shopDomain);
    }
    
    console.log('Sending status check to:', fullStatusUrl.toString());
    
    const response = await fetch(fullStatusUrl.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ predictionId })
    });
    
    if (!response.ok) {
      console.error('Status check failed with status:', response.status);
      const error = await response.json();
      throw new Error(error.error || `Erro ao verificar status (${response.status})`);
    }
    
    const data = await response.json();
    console.log('Status check response:', data);
    
    return data;
  }
}

// Export the class
window.ImageApiService = ImageApiService;
